import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { IdNegocio } from '../operaciones';
import type { components, operations } from '../tipos/disponibilidad';

/**
 * Operaciones de Agenda y Disponibilidad que usa el panel (3.1 a 3.4: «Horario» de Negocio, días especiales, festivos,
 * cierres de consulta, «Cuándo se puede reservar» y la vista previa de los cambios), con su `operationId` y los tipos
 * generados de `disponibilidad.yaml`.
 *
 * - Las que cambian un recurso editable (`updateCalendar`, `updateException`, `updateBookingRule`) piden `If-Match` con
 *   la versión leída: sin ella 422, con una vieja `412 version_conflict`.
 * - Las que crean llevan `Idempotency-Key` (obligatoria en `createCalendar`, `createWeeklySchedule`, `copyWeeklySchedule`
 *   y `createException`; opcional en el resto, que también la reciben para repetir la misma petición sin duplicarla).
 * - Un cambio que deja fuera reservas o citas ya hechas se confirma con el `change_id` de `previewScheduleChange` (en el
 *   cuerpo o, al anular o quitar, en la query, G250). Sin él, `409 commitments_pending` con la misma lista
 *   (`AffectedHoldsProblem`); caducado, `409 change_expired` (`code` del `Problem`, G856).
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Calendario = Esquema<'Calendar'>;
export type HorasEfectivas = Esquema<'EffectiveHours'>;
export type ListaCalendarios = Esquema<'CalendarList'>;
export type NuevoCalendario = Esquema<'CalendarCreate'>;
export type CambiosCalendario = Esquema<'CalendarUpdate'>;
export type TipoCalendario = Esquema<'CalendarKind'>;
export type Tramo = Esquema<'Interval'>;
export type TramoSemanal = Esquema<'WeeklyInterval'>;
export type SemanaTipo = Esquema<'WeeklySchedule'>;
export type ListaSemanas = Esquema<'WeeklyScheduleList'>;
export type NuevaSemana = Esquema<'WeeklyScheduleCreate'>;
export type SemanaGuardada = Esquema<'WeeklyScheduleSaved'>;
export type CopiaSemana = Esquema<'ScheduleCopy'>;
export type PeticionVistaPrevia = Esquema<'ScheduleChangePreviewRequest'>;
export type VistaPrevia = Esquema<'ScheduleChangePreview'>;
export type Afectado = Esquema<'AffectedHold'>;
export type ProblemaAfectados = Esquema<'AffectedHoldsProblem'>;
export type DiaEspecial = Esquema<'CalendarException'>;
export type ListaDiasEspeciales = Esquema<'CalendarExceptionList'>;
export type NuevoDiaEspecial = Esquema<'CalendarExceptionCreate'>;
export type CambiosDiaEspecial = Esquema<'CalendarExceptionUpdate'>;
export type Festivo = Esquema<'HolidayCatalogEntry'>;
export type CatalogoFestivos = Esquema<'HolidayCatalogList'>;
export type Cierre = Esquema<'Closure'>;
export type ListaCierres = Esquema<'ClosureList'>;
export type ReglaReserva = Esquema<'BookingRule'>;
export type ListaReglas = Esquema<'BookingRuleList'>;
export type ValoresRegla = Esquema<'BookingRuleValues'>;
export type Reservable = Esquema<'Bookable'>;
export type ListaReservables = Esquema<'BookableList'>;

type FiltrosCalendarios = NonNullable<operations['listCalendars']['parameters']['query']>;
type FiltrosCierres = NonNullable<operations['listClosures']['parameters']['query']>;
type FiltrosReglas = NonNullable<operations['listBookingRules']['parameters']['query']>;
type FiltrosReservables = NonNullable<operations['listBookables']['parameters']['query']>;
type FiltrosFestivos = operations['listHolidayCatalog']['parameters']['query'];

const base = (businessId: IdNegocio): `/v1/${string}` => `${negocio(businessId)}/availability`;
const calendario = (businessId: IdNegocio, calendarId: string): `/v1/${string}` => `${base(businessId)}/calendars/${encodeURIComponent(calendarId)}`;
const conCambio = (changeId: string | undefined) => consulta({ change_id: changeId });

/**
 * `ETag` de un día especial o una regla que llega en una lista (no hay `GET` de uno solo): la API lo manda en cada
 * elemento (`etag`, G370), el mismo valor que su cabecera `ETag`.
 */
export function versionDe(recurso: { etag: string }): string {
    return recurso.etag;
}

export function operacionesDisponibilidad(api: ClienteApi) {
    return {
        // ---- Calendarios ----
        /** listCalendars (`disponibilidad.horarios.ver`): el del negocio (`kind=business`) y uno por sede (`kind=location`). */
        listCalendars: (businessId: IdNegocio, filtros: FiltrosCalendarios = {}) =>
            api.pedir<ListaCalendarios>('GET', `${base(businessId)}/calendars${consulta({ ...filtros })}`),
        /** getCalendar: con `effective_timezone` y su `ETag` (para cambiar la zona con updateCalendar). */
        getCalendar: (businessId: IdNegocio, calendarId: string) => api.pedirConVersion<Calendario>('GET', calendario(businessId, calendarId)),
        /**
         * getEffectiveHours (`disponibilidad.horarios.ver`): los tramos de cada día del calendario entre dos fechas (el horario
         * del día en Reservas, G245, y cuándo trabaja cada profesional en la Agenda).
         */
        getEffectiveHours: (businessId: IdNegocio, calendarId: string, desde: string, hasta: string) =>
            api.pedir<HorasEfectivas>('GET', `${calendario(businessId, calendarId)}/effective-hours${consulta({ from: desde, to: hasta })}`),
        /**
         * createCalendar (`disponibilidad.calendario_negocio.gestionar`, G248): el calendario de una sede, la primera vez que
         * se activa «Horario propio» o se cambia su zona (heredar no lo necesita).
         */
        createCalendar: (businessId: IdNegocio, datos: NuevoCalendario, clave = claveIdempotencia()): Promise<ConVersion<Calendario>> =>
            api.pedirConVersion<Calendario>('POST', `${base(businessId)}/calendars`, { cuerpo: datos, idempotencia: clave }),
        /** updateCalendar: la zona horaria (`null` = la del negocio), con `If-Match` y, si hay afectados, `change_id` (G255). */
        updateCalendar: (businessId: IdNegocio, calendarId: string, cambios: CambiosCalendario, version: string, clave = claveIdempotencia()) =>
            api.pedirConVersion<Calendario>('PATCH', calendario(businessId, calendarId), { cuerpo: cambios, siCoincide: version, idempotencia: clave }),

        // ---- Semana tipo ----
        /** listWeeklySchedules: versiones de la semana tipo (vigente, programada, anteriores). */
        listWeeklySchedules: (businessId: IdNegocio, calendarId: string, pagina: Pagina = {}) =>
            api.pedir<ListaSemanas>('GET', `${calendario(businessId, calendarId)}/weekly-schedules${consulta({ ...pagina })}`),
        /**
         * createWeeklySchedule: una versión nueva con su fecha de efecto (una semana nunca se sobrescribe). Con `warnings`
         * (`outside_business_hours`: el profesional trabaja fuera del horario del negocio; se guarda igual, G437).
         */
        createWeeklySchedule: (businessId: IdNegocio, calendarId: string, semana: NuevaSemana, clave = claveIdempotencia()) =>
            api.pedir<SemanaGuardada>('POST', `${calendario(businessId, calendarId)}/weekly-schedules`, { cuerpo: semana, idempotencia: clave }),
        /** withdrawWeeklySchedule: anular una semana programada; con afectados, el `change_id` de la vista previa (G250). */
        withdrawWeeklySchedule: (businessId: IdNegocio, calendarId: string, scheduleId: string, changeId?: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('DELETE', `${calendario(businessId, calendarId)}/weekly-schedules/${encodeURIComponent(scheduleId)}${conCambio(changeId)}`, { idempotencia: clave }),
        /** copyWeeklySchedule: la semana de este calendario a otras sedes, como horario propio (no toca sus días especiales). */
        copyWeeklySchedule: (businessId: IdNegocio, calendarId: string, copia: CopiaSemana, clave = claveIdempotencia()) =>
            api.pedir<{ data: SemanaTipo[] }>('POST', `${calendario(businessId, calendarId)}/schedule-copies`, { cuerpo: copia, idempotencia: clave }),
        /** previewScheduleChange: qué reservas o citas deja fuera un cambio, sin guardarlo (vale 15 minutos). */
        previewScheduleChange: (businessId: IdNegocio, peticion: PeticionVistaPrevia, clave = claveIdempotencia()) =>
            api.pedir<VistaPrevia>('POST', `${base(businessId)}/schedule-change-previews`, { cuerpo: peticion, idempotencia: clave }),

        // ---- Días especiales y festivos ----
        /** listExceptions: los días especiales vivos entre `from` y `to` (92 días como mucho). */
        listExceptions: (businessId: IdNegocio, calendarId: string, desde: string, hasta: string, pagina: Pagina = {}) =>
            api.pedir<ListaDiasEspeciales>('GET', `${calendario(businessId, calendarId)}/exceptions${consulta({ from: desde, to: hasta, ...pagina })}`),
        /** createException: cerrado, horario especial o festivo (del catálogo con `holiday_code`, o local con su motivo). */
        createException: (businessId: IdNegocio, calendarId: string, dia: NuevoDiaEspecial, clave = claveIdempotencia()) =>
            api.pedirConVersion<DiaEspecial>('POST', `${calendario(businessId, calendarId)}/exceptions`, { cuerpo: dia, idempotencia: clave }),
        /** updateException: con `If-Match` y, si hay afectados, `change_id`. */
        updateException: (businessId: IdNegocio, calendarId: string, exceptionId: string, cambios: CambiosDiaEspecial, version: string, clave = claveIdempotencia()) =>
            api.pedirConVersion<DiaEspecial>('PATCH', `${calendario(businessId, calendarId)}/exceptions/${encodeURIComponent(exceptionId)}`, { cuerpo: cambios, siCoincide: version, idempotencia: clave }),
        /** withdrawException: quitar un día especial; con afectados, el `change_id` de la vista previa (G250). */
        withdrawException: (businessId: IdNegocio, calendarId: string, exceptionId: string, changeId?: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('DELETE', `${calendario(businessId, calendarId)}/exceptions/${encodeURIComponent(exceptionId)}${conCambio(changeId)}`, { idempotencia: clave }),
        /** listHolidayCatalog (G251): nacionales y, con `region`, los de la comunidad; con `calendar_id`, los que ya tiene. */
        listHolidayCatalog: (businessId: IdNegocio, filtros: FiltrosFestivos) =>
            api.pedir<CatalogoFestivos>('GET', `${base(businessId)}/holiday-catalog${consulta({ ...filtros })}`),

        // ---- Cierres y retenes (solo consulta: los crean Cierre de recursos y Sala) ----
        /** listClosures. */
        listClosures: (businessId: IdNegocio, filtros: FiltrosCierres = {}) =>
            api.pedir<ListaCierres>('GET', `${base(businessId)}/closures${consulta({ ...filtros })}`),
        /** listBookables: el nombre de cada zona o recurso (cierres, afectados y reglas propias). */
        listBookables: (businessId: IdNegocio, filtros: FiltrosReservables = {}) =>
            api.pedir<ListaReservables>('GET', `${base(businessId)}/bookables${consulta({ ...filtros })}`),

        // ---- Cuándo se puede reservar (3.3) ----
        /** listBookingRules: la del negocio (`target_kind=business`) y las propias de zonas, recursos y servicios. */
        listBookingRules: (businessId: IdNegocio, filtros: FiltrosReglas = {}) =>
            api.pedir<ListaReglas>('GET', `${base(businessId)}/booking-rules${consulta({ ...filtros })}`),
        /** updateBookingRule (`disponibilidad.reglas.gestionar`): con `If-Match`; no cambia lo ya reservado. */
        updateBookingRule: (businessId: IdNegocio, ruleId: string, valores: ValoresRegla, version: string, clave = claveIdempotencia()) =>
            api.pedirConVersion<ReglaReserva>('PATCH', `${base(businessId)}/booking-rules/${encodeURIComponent(ruleId)}`, { cuerpo: valores, siCoincide: version, idempotencia: clave }),
    };
}
